interface DashboardPreferences {
  layout: {
    metricsPosition: 'top' | 'bottom';
    chartType: 'area' | 'line' | 'bar';
    activityFeedPosition: 'right' | 'bottom';
    showConnectionStatus: boolean;
    refreshInterval: number; // in seconds
  };
  display: {
    compactMode: boolean;
    animationsEnabled: boolean;
    showTrends: boolean;
    activityLimit: number;
  };
  export: {
    defaultFormat: 'csv' | 'json';
    includeMetadata: boolean;
  };
}

const DEFAULT_PREFERENCES: DashboardPreferences = {
  layout: {
    metricsPosition: 'top',
    chartType: 'area',
    activityFeedPosition: 'right',
    showConnectionStatus: true,
    refreshInterval: 30,
  },
  display: {
    compactMode: false,
    animationsEnabled: true,
    showTrends: true,
    activityLimit: 50,
  },
  export: {
    defaultFormat: 'csv',
    includeMetadata: true,
  },
};

class PreferencesService {
  private readonly STORAGE_KEY = 'dashboard_preferences';

  getPreferences(): DashboardPreferences {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all properties exist
        return this.mergeWithDefaults(parsed);
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
    }
    return DEFAULT_PREFERENCES;
  }

  savePreferences(preferences: Partial<DashboardPreferences>): void {
    try {
      const current = this.getPreferences();
      const updated = this.deepMerge(current, preferences);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));

      // Dispatch custom event for preference changes
      window.dispatchEvent(new CustomEvent('dashboard-preferences-changed', {
        detail: updated,
      }));
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  }

  resetPreferences(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('dashboard-preferences-changed', {
        detail: DEFAULT_PREFERENCES,
      }));
    } catch (error) {
      console.error('Failed to reset preferences:', error);
    }
  }

  private mergeWithDefaults(saved: any): DashboardPreferences {
    return this.deepMerge(DEFAULT_PREFERENCES, saved);
  }

  private deepMerge(target: any, source: any): any {
    const output = { ...target };

    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }

    return output;
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }
}

export const preferencesService = new PreferencesService();
export type { DashboardPreferences };
